import { collapseParentRollupPeriods, type ParentRollupQuery, reportingKeys } from '@taler/api-client'
import type { Locale } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { buildRollupTree, type RollupTreeRow } from '../internal/rollup-tree.ts'
import { useRollupAccounts } from './use-rollup-accounts.ts'

export interface UseParentRollupOptions {
  readonly ledgerId: () => string | undefined
  readonly query: () => ParentRollupQuery
  readonly locale: () => Locale
  readonly enabled: () => boolean
}

export interface UseParentRollup {
  readonly rows: ComputedRef<readonly RollupTreeRow[] | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

/** The one place this screen fetches: the rollup plus the account names it renders under it. */
export function useParentRollup(options: UseParentRollupOptions): UseParentRollup {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const rollupQuery = useQuery({
    queryKey: computed(() => [
      ...reportingKeys.parentRollup(options.ledgerId() ?? '', options.query()),
      options.query().class,
    ]),
    queryFn: ({ signal }) => api.reporting.parentRollup(options.ledgerId()!, options.query(), signal),
    enabled: active,
    retry: false,
  })

  const names = useRollupAccounts({
    ledgerId: options.ledgerId,
    accountClass: () => options.query().class,
    enabled: options.enabled,
  })

  const isLoading = computed(() => active.value && (rollupQuery.isPending.value || names.isLoading.value))
  const error = computed(() => rollupQuery.error.value ?? names.error.value)
  const rollupRetrying = retrying(rollupQuery)
  const rows = computed(() => {
    if (rollupQuery.data.value === undefined || names.accounts.value === undefined) return undefined
    return buildRollupTree(
      collapseParentRollupPeriods(rollupQuery.data.value),
      names.accounts.value,
      options.locale(),
    )
  })

  return {
    rows,
    isLoading,
    error,
    retrying: computed(() => rollupRetrying.value || names.retrying.value),
    retry: () => {
      void rollupQuery.refetch()
      names.retry()
    },
  }
}
